import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {Section} from "../components/section/section.tsx";

import EmailSubscriptionSwitch from "./email-subscription-switch.tsx";
import type {EmailSubscriptions} from "./types.ts";

interface Props {
    answerNotification: EmailSubscriptions["answerNotificationEmailSubscription"];
    assignmentNotification: EmailSubscriptions["newAssignmentNotificationEmailSubscription"];
    streaksNotification: EmailSubscriptions["streaksEmailSubscription"];
}

/**
 * A component to manage email settings for activity reports.
 */
export const NotificationsSection: React.FC<Props> = ({
    answerNotification,
    assignmentNotification,
    streaksNotification,
}) => {
    const [showError, setShowError] = useState(false);
    return (
        <Section
            legendContent={t`Notifications`}
            children={
                <View style={styles.container}>
                    <View style={styles.checkboxContainer}>
                        <EmailSubscriptionSwitch
                            checked={!!answerNotification?.checked}
                            label={t`Answer posted to your question`}
                            subscriptionKey={answerNotification?.key}
                            showError={() => setShowError(true)}
                            clearError={() => setShowError(false)}
                            inputId="answerNotification"
                        />
                        <EmailSubscriptionSwitch
                            checked={!!assignmentNotification?.checked}
                            label={t`New assignment notifications`}
                            subscriptionKey={assignmentNotification?.key}
                            showError={() => setShowError(true)}
                            clearError={() => setShowError(false)}
                            inputId="assignmentNotification"
                        />
                        <EmailSubscriptionSwitch
                            checked={!!streaksNotification?.checked}
                            label={t`Notifications related to streaks`}
                            subscriptionKey={streaksNotification?.token}
                            showError={() => setShowError(true)}
                            clearError={() => setShowError(false)}
                            inputId="streaksNotification"
                        />
                    </View>

                    {showError && (
                        <LabelSmall style={styles.errorText}>
                            {t`Oops! Something went wrong. Please try again.`}
                        </LabelSmall>
                    )}
                </View>
            }
        />
    );
};

const styles = StyleSheet.create({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_200,
    },
    checkboxContainer: {
        display: "flex",
        flexDirection: "column",
        gap: sizing.size_080,
    },
    errorText: {
        color: semanticColor.status.critical.foreground,
        marginBlockStart: sizing.size_040,
    },
});

export default NotificationsSection;
